import { AlarmVideo, Box, Frame, UnifiedAlarm } from '@hakimo-ui/hakimo/types';
import { FramePlayer, MultiVideoPlayer } from '@hakimo-ui/hakimo/ui-elements';
import { useMemo, useState } from 'react';

interface Props {
  alarms: UnifiedAlarm[];
  mediaType: 'video' | 'frames';
  timeZone: string;
  currentPlayingAlarmId: string;
  onPlayingAlarmChange: (alarmId: string) => void;
  showMatchedFaces?: boolean;
}

export function EventMedia(props: Props) {
  const {
    alarms,
    mediaType,
    timeZone,
    currentPlayingAlarmId,
    onPlayingAlarmChange,
    showMatchedFaces = false,
  } = props;
  const [currentFrameIndex, setCurrentFrameIndex] = useState(0);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  const allAlarmsVideos = useMemo(() => {
    return alarms
      .sort((a, b) => a.alarmStartTime - b.alarmStartTime)
      .reduce((acc, alarm) => {
        const alarmVideo = alarm.media.video;
        if (alarmVideo) {
          acc.push(alarmVideo);
        }
        return acc;
      }, [] as AlarmVideo[]);
  }, [alarms]);

  const videoUrlToBoxesMap = useMemo(() => {
    return alarms.reduce((acc, alarm) => {
      const alarmVideo = alarm.media.video;
      if (alarmVideo) {
        acc[alarmVideo.url] = alarmVideo.boxes;
      }
      return acc;
    }, {} as Record<string, Box[]>);
  }, [alarms]);

  const allAlarmsFrames = useMemo(() => {
    return alarms
      .sort((a, b) => a.alarmStartTime - b.alarmStartTime)
      .reduce((acc, alarm) => {
        const alarmFrames =
          alarm.media?.frames?.map((frame) => ({
            ...frame,
            id: alarm.id,
            cameraName: alarm.cameraName,
            alarmType: alarm.alarm_type,
          })) ?? [];
        if (alarmFrames) {
          acc.push(...alarmFrames);
        }
        return acc;
      }, [] as Frame[]);
  }, [alarms]);

  const onFrameChange = (idx: number) => {
    const alarmId = allAlarmsFrames[idx]?.id ?? '';
    if (alarmId !== currentPlayingAlarmId) {
      onPlayingAlarmChange(alarmId);
    }
    setCurrentFrameIndex(idx);
  };

  const onNextVideo = () => {
    setCurrentVideoIndex((prev) => {
      let newVideoIndex;
      if (prev + 1 >= allAlarmsVideos.length) {
        newVideoIndex = 0;
      } else {
        newVideoIndex = prev + 1;
      }
      onPlayingAlarmChange(alarms[newVideoIndex]?.id);
      return newVideoIndex;
    });
  };

  return mediaType === 'video' ? (
    <MultiVideoPlayer
      currentVideoIndex={currentVideoIndex}
      onNextVideo={onNextVideo}
      videosUrls={allAlarmsVideos.map((video) => video.url)}
      videoUrlToBoxesMap={videoUrlToBoxesMap}
    />
  ) : (
    <FramePlayer
      frames={allAlarmsFrames}
      currentFrameIndex={currentFrameIndex}
      onFrameChange={onFrameChange}
      timeZone={timeZone}
      showMatchedFaces={showMatchedFaces}
    />
  );
}
